import React from 'react';
import { Placeholder } from '@sitecore-jss/sitecore-jss-react';
import { AdsAdvancedBackground } from '@arm/arm-design-system-react';
import RenderingStyling from '../../../lib/RenderingStyling';
import { addStyle, getBackgroundImages } from '../SharedUtils/utils';
import { useTheme } from '../SharedUtils/hooks';
import { getBackgroundColor } from '../SharedUtils/themeColors';

const AdvancedBackground = ({ fields, rendering }) => {
  const mapBackgroundType = (type) => {
    switch (type) {
      case 'Colour':
        return 'Color';
      case 'Gradient':
        return 'Gradient';
      case 'Image':
        return 'Image';
      case 'Image and Colour':
        return 'Image Color';
      case 'Image and Gradient':
        return 'Image Gradient';
      case 'Video':
        return 'Video';
      default:
        return 'position';
    }
  };

  const { isLight } = useTheme();

  const widgetStyleList = {};

  let paddingTop = fields.PaddingTop?.value;
  let paddingBottom = fields.PaddingBottom?.value;

  if (!paddingTop) {
    paddingTop = '90px';
  }

  if (!paddingBottom) {
    paddingBottom = '45px';
  }

  const backgroundAttachment = fields.backgroundAttachment?.fields?.value?.value;
  const backgroundBlendMode = fields.backgroundBlendMode?.fields?.value?.value;
  const backgroundRepeat = fields.backgroundRepeat?.fields?.value?.value;
  const backgroundPosition = fields.backgroundPosition?.value;
  const mixBlenMode = fields.mixBlenMode?.fields?.value?.value;
  const backgroundType = fields.BackgroundType?.fields?.value?.value;
  const gradientType =
    fields.GradientType?.fields?.value?.value == 'LeftRight' ? 'to right' : 'to bottom';

  const startColor = isLight
    ? fields.GradientStartColorHexCodeLight?.value
    : fields.GradientStartColorHexCodeDark?.value;
  const endColor = isLight
    ? fields.GradientEndColorHexCodeLight?.value
    : fields.GradientEndColorHexCodeDark?.value;

  const backgroundColor = getBackgroundColor(fields, isLight);
  const backgroundSize =
    fields.backgroundSize?.fields?.value?.value == 'has-background-cover'
      ? 'cover'
      : fields.backgroundSize?.fields?.value?.value;

  const enableParallax = fields.enableVideoBackgroundParallax?.value;

  const useInvertedColor = isLight
    ? fields.useInvertedColorForVideoControlLight?.value
    : fields.useInvertedColorForVideoControlDark?.value;
  const playText = fields.playText?.value;
  const pauseText = fields.pauseText?.value;
  const enableVideoControl = fields.enableVideoControl?.value;

  const largeVideoSrc = fields.videoUrl?.value?.src;
  const mediumVideoSrc = fields.VideoUrlMedium?.value?.src;
  const smallVideoSrc = fields.VideoUrlSmall?.value?.src;

  addStyle(widgetStyleList, '--ads-advanced-background-padding-top', paddingTop);
  addStyle(widgetStyleList, '--ads-advanced-background-padding-bottom', paddingBottom);

  addStyle(widgetStyleList, '--ads-advanced-background-attachment', backgroundAttachment);
  addStyle(widgetStyleList, '--ads-advanced-background-blend-mode', backgroundBlendMode);

  addStyle(widgetStyleList, '--ads-advanced-background-color', backgroundColor);

  addStyle(widgetStyleList, '--ads-advanced-background-gradient-end-color', endColor);
  addStyle(widgetStyleList, '--ads-advanced-background-gradient-start-color', startColor);

  addStyle(widgetStyleList, '--ads-advanced-background-gradient-type', gradientType);
  addStyle(widgetStyleList, '--ads-advanced-background-mix-blend-mode', mixBlenMode);

  addStyle(widgetStyleList, '--ads-advanced-background-position', backgroundPosition ?? 'center');
  addStyle(widgetStyleList, '--ads-advanced-background-repeat', backgroundRepeat);
  addStyle(widgetStyleList, '--ads-advanced-background-size', backgroundSize);

  const backgroundTypeMapped = mapBackgroundType(backgroundType);

  const {
    largeBackgroundImageSrc: backgroundImageLargeSrc,
    mediumBackgroundImageSrc: backgroundImageMediumSrc,
    smallBackgroundImageSrc: backgroundImageSmallSrc,
  } = getBackgroundImages(fields, isLight);

  let backgroundImages = {};

  if (backgroundImageLargeSrc) {
    backgroundImages.imageUrlLarge = `{"imgUrl": "${backgroundImageLargeSrc}", "breakpoint": 1024}`;
  }

  if (backgroundImageMediumSrc) {
    backgroundImages.imageUrlMedium = `{"imgUrl": "${backgroundImageMediumSrc}", "breakpoint": 768}`;
  }

  if (backgroundImageSmallSrc) {
    backgroundImages.imageUrlSmall = `{"imgUrl": "${backgroundImageSmallSrc}"}`;
  }

  let backgroundVideos = {};

  if (largeVideoSrc) {
    backgroundVideos.videoUrlLarge = `{"videoUrl": "${largeVideoSrc}"}`;
  }

  if (mediumVideoSrc) {
    backgroundVideos.videoUrlMedium = `{"videoUrl": "${mediumVideoSrc}"}`;
  }

  if (smallVideoSrc) {
    backgroundVideos.videoUrlSmall = `{"videoUrl": "${smallVideoSrc}"}`;
  }

  return (
    <AdsAdvancedBackground
      backgroundType={backgroundTypeMapped}
      enableParallax={enableParallax}
      enableVideoControl={enableVideoControl}
      enableVideoControlText={true}
      useInvertedColorVideoControl={useInvertedColor}
      {...(backgroundType !== 'Gradient' && { ...backgroundImages })}
      pauseText={pauseText}
      playText={playText}
      {...backgroundVideos}
      style={widgetStyleList}
      {...RenderingStyling(rendering.componentName)}
    >
      <Placeholder name="jss-multiple-content" rendering={rendering} />
    </AdsAdvancedBackground>
  );
};

export default AdvancedBackground;
